import { useRef, useEffect } from 'react';
import { useLocalObservable } from 'mobx-react';

export const useSpeechRecognition = (onTranscript) => {
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const onTranscriptRef = useRef(onTranscript);

  // Keep callback ref updated
  onTranscriptRef.current = onTranscript;

  const state = useLocalObservable(() => ({
    isListening: false,
    isSupported: false,
    error: null,
    speechState: 'idle', // 'idle', 'listening', 'detecting-speech', 'processing', 'completed'
    interimText: '',
    silenceTimer: null,

    setListening: (listening) => {
      state.isListening = listening;
    },
    setSupported: (supported) => {
      state.isSupported = supported;
    },
    setError: (error) => {
      state.error = error;
    },
    setSpeechState: (speechState) => {
      state.speechState = speechState;
    },
    setInterimText: (text) => {
      state.interimText = text;
    }
  }));

  // Initialize speech recognition
  useEffect(() => {
    // Prevent multiple initializations
    if (recognitionRef.current) {
      return;
    }

    // Check if we're in a secure context (HTTPS or localhost)
    if (!window.isSecureContext) {
      console.warn('Speech recognition requires secure context');
      const isLocalhost = location.hostname === 'localhost' || location.hostname === '127.0.0.1';
      if (isLocalhost) {
        state.setError('For HTTP localhost: Enable chrome://flags/#unsafely-treat-insecure-origin-as-secure and add ' + location.origin);
      } else {
        state.setError('Speech recognition requires HTTPS');
      }
      state.setSupported(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.warn('Speech recognition not supported in this browser');
      state.setSupported(false);
      return;
    }


    state.setSupported(true);

    const recognition = new SpeechRecognition();
    recognition.continuous = false; // Start with false to avoid Chrome issues
    recognition.interimResults = true; // Show real-time progress
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;

    // Additional settings to help with network issues (Safari doesn't support SpeechGrammarList)
    const SpeechGrammarList = window.SpeechGrammarList || window.webkitSpeechGrammarList;
    if (SpeechGrammarList) {
      recognition.grammars = new SpeechGrammarList();
    }

    // Enhanced event handlers for better UX
    recognition.onstart = () => {
      // Only process if this is the current recognition object
      if (recognitionRef.current === recognition) {
        state.setListening(true);
        state.setSpeechState('listening');
        state.setError(null);
        state.setInterimText('');
      } else {
        return;
      }

      // Clear any existing timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Overall timeout - stop if no activity for 30 seconds
      timeoutRef.current = setTimeout(() => {
        console.warn('⏰ Speech recognition timeout - no activity after 30 seconds');
        if (state.isListening) {
          recognition.stop();
          state.setError('No speech detected. Please try again.');
        }
      }, 30000);
    };

    // Detect when speech actually starts
    recognition.onspeechstart = () => {
      state.setSpeechState('detecting-speech');

      // Clear the long timeout since we detected speech
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };

    // Detect when speech ends (natural pause)
    recognition.onspeechend = () => {
      state.setSpeechState('processing');

      // Auto-stop after 3 seconds of silence
      timeoutRef.current = setTimeout(() => {
        recognition.stop();
      }, 3000);
    };

    // Detect audio input start/stop for better feedback
    recognition.onsoundstart = () => {};
    recognition.onsoundend = () => {};

    recognition.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';

      // Process all results to separate interim and final text
      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        const isFinal = event.results[i].isFinal;

        if (isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      // Update interim text for real-time feedback
      if (interimTranscript) {
        state.setInterimText(interimTranscript);
        state.setSpeechState('detecting-speech');
      }

      // Handle final result
      if (finalTranscript.trim()) {
        state.setSpeechState('completed');
        state.setInterimText('');

        if (onTranscriptRef.current) {
          onTranscriptRef.current(finalTranscript.trim());
        }

        // Auto-stop after getting final result
        setTimeout(() => {
          if (state.isListening) {
            recognition.stop();
          }
        }, 100);
      }

      // Reset silence timer when we get new results
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);

      // Provide more specific error messages
      let errorMessage;
      switch (event.error) {
        case 'network':
          errorMessage = `Network error: Cannot reach Google Speech servers. Try: 1) Check internet, 2) Disable VPN, 3) Try incognito mode, 4) Check firewall settings`;
          break;
        case 'not-allowed':
          errorMessage = 'Microphone access denied. Please allow microphone permissions.';
          break;
        case 'no-speech':
          errorMessage = 'No speech detected. Try speaking more clearly.';
          break;
        case 'audio-capture':
          errorMessage = 'No microphone found. Check your audio devices.';
          break;
        case 'service-not-allowed':
          errorMessage = 'Speech service not allowed. Try refreshing the page.';
          break;
        default:
          errorMessage = `Speech recognition error: ${event.error}`;
      }

      state.setError(errorMessage);
      state.setListening(false);

      const currentTimeout = timeoutRef.current;
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
    };

    recognition.onend = () => {
      // Check if this is the current recognition object
      if (recognitionRef.current === recognition) {
        state.setListening(false);
        state.setSpeechState('idle');
        state.setInterimText('');

        // Clean up timeouts
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      const currentRecognition = recognitionRef.current;
      const currentTimeout = timeoutRef.current;

      if (currentRecognition) {
        currentRecognition.abort();
      }
      if (currentTimeout) {
        clearTimeout(currentTimeout);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Note: Dependencies intentionally minimal to prevent speech recognition re-creation
  // onTranscript should be stable (wrapped in useCallback by parent if needed)

  const startListening = async () => {
    if (!state.isSupported) {
      console.warn('Speech recognition not supported');
      return false;
    }

    if (state.isListening) {
      console.warn('Already listening');
      return false;
    }

    if (!recognitionRef.current) {
      console.error('❌ No recognition object available - useEffect may not have completed');
      return false;
    }

    try {
      // Test microphone access - this will prompt for permissions if needed
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Clean up the test stream immediately
      stream.getTracks().forEach(track => track.stop());

      // Clear any previous permission errors
      state.setError(null);

      // Now start speech recognition
      recognitionRef.current?.start();
      return true;

    } catch (permissionError) {
      console.error('Microphone permission denied:', permissionError);

      // Provide user-friendly error messages based on the error type
      let errorMessage;
      if (permissionError.name === 'NotAllowedError') {
        errorMessage = 'Microphone access denied. Please allow microphone permissions in your browser settings and try again.';
      } else if (permissionError.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please check your audio devices.';
      } else if (permissionError.name === 'NotSupportedError') {
        errorMessage = 'Microphone access not supported in this browser.';
      } else {
        errorMessage = `Microphone access failed: ${permissionError.message}`;
      }

      state.setError(errorMessage);
      return false;
    }
  };

  const stopListening = () => {
    if (!state.isListening) {
      return;
    }

    try {
      recognitionRef.current?.stop();
    } catch (error) {
      console.error('Failed to stop speech recognition:', error);
      state.setError(error.message);
    }
  };

  const toggleListening = () => {
    if (state.isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  return {
    isListening: state.isListening,
    isSupported: state.isSupported,
    error: state.error,
    speechState: state.speechState,
    interimText: state.interimText,
    startListening,
    stopListening,
    toggleListening
  };
};